/**
 * KPI (thêm 22/09/2026, thay màn «Nhật ký công việc» trên menu): phân bổ công việc theo dự án, cân đối thời
 * gian và theo dõi KPI hằng tuần của nhân viên.
 *
 *   - Mỗi dự án có GIỜ / SẢN PHẨM (`hoursPerUnit`, ghi trên dự án).
 *   - Mỗi người có CẤP BẬC và HỆ SỐ (`kpistaff`, quản lý đặt); giờ chuẩn tuần = 40 × hệ số.
 *   - Mỗi tuần, với từng dự án người đó phụ trách hoặc được giao, quản lý đặt KPI TUẦN (số sản phẩm,
 *     `kpiweek`); giờ được giao = KPI × giờ/SP; tổng tuần = cộng các dự án, so với giờ chuẩn.
 *
 * Hàm thuần, có test.
 */
import type { RecordDto } from '@halo/shared';
import { toCsv } from './csv';
import { addDays, formatFullDate, type CalendarDate, type WeekColumn } from './dates';
import { normalizeForSearch } from './format';
import { compactData, readNumber, readString, type RecordData } from './records/parsing';
import { projectLead, type Project } from './records/project';

/** Giờ làm chuẩn của một tuần với hệ số 1. */
export const STANDARD_WEEK_HOURS = 40;

/** Gợi ý cấp bậc — ô vẫn gõ tự do. */
export const RANK_SUGGESTIONS = ['Thực tập', 'Nhân viên', 'Senior', 'Leader', 'Quản lý'] as const;

export interface KpiStaff {
  recordId: number;
  username: string;
  rank: string;
  /** Hệ số ≥ 0; mặc định 1. */
  coefficient: number;
}

export function parseKpiStaff(record: RecordDto): KpiStaff {
  const coefficient = readNumber(record.data, 'coef');
  return {
    recordId: record.id,
    username: readString(record.data, 'u'),
    rank: readString(record.data, 'rank').trim(),
    coefficient: coefficient === null ? 1 : Math.max(0, coefficient),
  };
}

export function serializeKpiStaff(staff: Omit<KpiStaff, 'recordId'>): RecordData {
  return compactData({ u: staff.username, rank: staff.rank.trim(), coef: staff.coefficient });
}

export interface KpiWeek {
  recordId: number;
  username: string;
  projectId: number;
  /** «DD/MM/YYYY» của ngày Chủ nhật. */
  weekKey: string;
  /** Số sản phẩm đặt cho tuần. */
  kpi: number;
}

export function parseKpiWeek(record: RecordDto): KpiWeek {
  return {
    recordId: record.id,
    username: readString(record.data, 'u'),
    projectId: readNumber(record.data, 'pj') ?? 0,
    weekKey: readString(record.data, 'w'),
    kpi: Math.max(0, readNumber(record.data, 'kpi') ?? 0),
  };
}

export function serializeKpiWeek(week: Omit<KpiWeek, 'recordId'>): RecordData {
  return compactData({ u: week.username, pj: week.projectId, w: week.weekKey, kpi: week.kpi });
}

export type KpiRole = 'lead' | 'assignee';

export interface KpiProjectRow {
  project: Project;
  role: KpiRole;
  hoursPerUnit: number;
  kpi: number;
  /** = KPI × giờ/SP. */
  hours: number;
  /** Bản ghi tuần đang lưu (để sửa), null = chưa đặt. */
  weekRecordId: number | null;
}

export interface KpiEmployeeRow {
  username: string;
  rank: string;
  coefficient: number;
  /** Bản ghi cấp bậc / hệ số đang lưu, null = chưa đặt. */
  staffRecordId: number | null;
  projects: KpiProjectRow[];
  totalHours: number;
  /** Giờ chuẩn tuần = 40 × hệ số. */
  capacityHours: number;
}

/** Bản ghi mới nhất (id lớn hơn) thắng khi trùng khoá. */
function latestBy<T extends { recordId: number }>(
  list: readonly T[],
  keyOf: (item: T) => string,
): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of list) {
    const key = keyOf(item);
    const current = map.get(key);
    if (!current || current.recordId < item.recordId) map.set(key, item);
  }
  return map;
}

/** Vai trò của một người trên dự án: phụ trách chính, được giao, hoặc không liên quan. */
export function kpiRoleOf(project: Project, username: string): KpiRole | null {
  if (projectLead(project) === username) return 'lead';
  if (project.assignees.includes(username)) return 'assignee';
  return null;
}

/** Bản ghi KPI của một người cho một dự án trong tuần `weekKey` (mới nhất nếu trùng), hoặc null. */
export function findKpiWeek(
  weeks: readonly KpiWeek[],
  username: string,
  projectId: number,
  weekKey: string,
): KpiWeek | null {
  let found: KpiWeek | null = null;
  for (const week of weeks) {
    if (week.username !== username || week.projectId !== projectId || week.weekKey !== weekKey) {
      continue;
    }
    if (!found || found.recordId < week.recordId) found = week;
  }
  return found;
}

/**
 * Dòng KPI của tuần `weekKey` cho những người trong `people` (theo thứ tự đưa vào): mỗi người có các dự
 * án CHƯA XONG mà họ phụ trách hoặc được giao. Người chưa có dự án nào: bỏ qua, trừ khi `keepIdle` —
 * bảng nhập liệu cần cả họ để đặt cấp bậc / hệ số và giao dự án.
 */
export function buildKpiRows(
  people: readonly string[],
  projects: readonly Project[],
  staff: readonly KpiStaff[],
  weeks: readonly KpiWeek[],
  weekKey: string,
  options: { keepIdle?: boolean } = {},
): KpiEmployeeRow[] {
  const staffByUser = latestBy(staff, (item) => item.username);
  const weekByKey = latestBy(
    weeks.filter((week) => week.weekKey === weekKey),
    (week) => `${week.username}|${week.projectId}`,
  );
  const open = projects.filter((project) => project.status !== 'done');
  const rows: KpiEmployeeRow[] = [];
  for (const username of people) {
    const mine: KpiProjectRow[] = [];
    for (const project of open) {
      const role = kpiRoleOf(project, username);
      if (!role) continue;
      const entry = weekByKey.get(`${username}|${project.recordId}`);
      const kpi = entry?.kpi ?? 0;
      mine.push({
        project,
        role,
        hoursPerUnit: project.hoursPerUnit,
        kpi,
        hours: kpi * project.hoursPerUnit,
        weekRecordId: entry?.recordId ?? null,
      });
    }
    if (!mine.length && !options.keepIdle) continue;
    const info = staffByUser.get(username);
    const coefficient = info?.coefficient ?? 1;
    rows.push({
      username,
      rank: info?.rank ?? '',
      coefficient,
      staffRecordId: info?.recordId ?? null,
      projects: mine,
      totalHours: mine.reduce((sum, item) => sum + item.hours, 0),
      capacityHours: STANDARD_WEEK_HOURS * coefficient,
    });
  }
  return rows;
}

/** Lọc theo tên người (hiển thị hoặc đăng nhập) hay tên / mã dự án, không phân biệt dấu. */
export function filterKpiRows(
  rows: readonly KpiEmployeeRow[],
  query: string,
  displayName: (username: string) => string,
): KpiEmployeeRow[] {
  const needle = normalizeForSearch(query);
  if (!needle) return [...rows];
  return rows.flatMap((row) => {
    if (normalizeForSearch(`${displayName(row.username)} ${row.username}`).includes(needle)) {
      return [row];
    }
    const projects = row.projects.filter((item) =>
      normalizeForSearch(`${item.project.title} ${item.project.code}`).includes(needle),
    );
    return projects.length ? [{ ...row, projects }] : [];
  });
}

/** «21/09/2026 – 27/09/2026». */
export function kpiWeekLabel(weekStart: CalendarDate): string {
  return `${formatFullDate(weekStart)} – ${formatFullDate(addDays(weekStart, 6))}`;
}

/** Giờ: nguyên thì «12», lẻ thì «1,5». */
export function formatHours(hours: number): string {
  return Number.isInteger(hours)
    ? String(hours)
    : hours.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
}

// ------------------------------------------------------------------ đánh giá nhân sự theo tuần

/** Ba tiêu chí giao ban thứ Hai (chủ dự án 22/09/2026), chấm 0–10. */
export const EVALUATION_CRITERIA = [
  { key: 'skill', label: 'Chuyên môn', dataKey: 'cm' },
  { key: 'compliance', label: 'Thực hiện quy định công ty', dataKey: 'qd' },
  { key: 'attitude', label: 'Ý thức làm việc', dataKey: 'yt' },
] as const;
export type EvaluationCriterion = (typeof EVALUATION_CRITERIA)[number]['key'];
export type EvaluationScores = Record<EvaluationCriterion, number | null>;
export const MAX_SCORE = 10;
export const EMPTY_SCORES: EvaluationScores = { skill: null, compliance: null, attitude: null };

export interface KpiEvaluation {
  recordId: number;
  username: string;
  /** «DD/MM/YYYY» của ngày Chủ nhật. */
  weekKey: string;
  /** null = chưa chấm tiêu chí đó. */
  scores: EvaluationScores;
  note: string;
  /** Người chấm lần cuối, lúc nào (ISO). */
  by: string;
  at: string;
}

function readScore(data: RecordData, key: string): number | null {
  const value = readNumber(data, key);
  return value === null ? null : Math.min(MAX_SCORE, Math.max(0, value));
}

export function parseKpiEvaluation(record: RecordDto): KpiEvaluation {
  const data = record.data;
  return {
    recordId: record.id,
    username: readString(data, 'u'),
    weekKey: readString(data, 'w'),
    scores: {
      skill: readScore(data, 'cm'),
      compliance: readScore(data, 'qd'),
      attitude: readScore(data, 'yt'),
    },
    note: readString(data, 'note'),
    by: readString(data, 'by'),
    at: readString(data, 'at'),
  };
}

/** Tiêu chí chưa chấm thì KHÔNG ghi khoá — phân biệt «chưa chấm» với «0 điểm». */
export function serializeKpiEvaluation(evaluation: Omit<KpiEvaluation, 'recordId'>): RecordData {
  return compactData({
    u: evaluation.username,
    w: evaluation.weekKey,
    cm: evaluation.scores.skill ?? undefined,
    qd: evaluation.scores.compliance ?? undefined,
    yt: evaluation.scores.attitude ?? undefined,
    note: evaluation.note.trim() || undefined,
    by: evaluation.by,
    at: evaluation.at,
  });
}

/** Trung bình các tiêu chí ĐÃ chấm; chưa chấm gì thì null. */
export function evaluationAverage(scores: EvaluationScores): number | null {
  const rated = EVALUATION_CRITERIA.map((item) => scores[item.key]).filter(
    (value): value is number => value !== null,
  );
  return rated.length ? rated.reduce((sum, value) => sum + value, 0) / rated.length : null;
}

export function isComplete(scores: EvaluationScores): boolean {
  return EVALUATION_CRITERIA.every((item) => scores[item.key] !== null);
}

export interface Grade {
  label: string;
  tone: 'success' | 'processing' | 'default' | 'warning' | 'error';
}

const GRADE_STEPS: readonly [number, Grade][] = [
  [9, { label: 'Xuất sắc', tone: 'success' }],
  [8, { label: 'Tốt', tone: 'processing' }],
  [6.5, { label: 'Khá', tone: 'default' }],
  [5, { label: 'Trung bình', tone: 'warning' }],
  [0, { label: 'Yếu', tone: 'error' }],
];

/** Xếp loại theo điểm trung bình — chỉ khi đã chấm ĐỦ ba tiêu chí. */
export function gradeOf(scores: EvaluationScores): Grade | null {
  if (!isComplete(scores)) return null;
  const average = evaluationAverage(scores) ?? 0;
  return GRADE_STEPS.find(([floor]) => average >= floor)?.[1] ?? null;
}

/** «8,5»; chưa chấm «—». */
export function formatScore(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('vi-VN', { maximumFractionDigits: 1 });
}

export interface EvaluationRow {
  username: string;
  rank: string;
  /** Bản ghi đánh giá của tuần, null = chưa chấm. */
  evaluation: KpiEvaluation | null;
  average: number | null;
  grade: Grade | null;
}

/** Bảng đánh giá tuần `weekKey` cho những người trong `people` (theo thứ tự đưa vào) — ai cũng có dòng. */
export function buildEvaluationRows(
  people: readonly string[],
  evaluations: readonly KpiEvaluation[],
  staff: readonly KpiStaff[],
  weekKey: string,
): EvaluationRow[] {
  const staffByUser = latestBy(staff, (item) => item.username);
  const byUser = latestBy(
    evaluations.filter((item) => item.weekKey === weekKey),
    (item) => item.username,
  );
  return people.map((username) => {
    const evaluation = byUser.get(username) ?? null;
    const scores = evaluation?.scores ?? EMPTY_SCORES;
    return {
      username,
      rank: staffByUser.get(username)?.rank ?? '',
      evaluation,
      average: evaluationAverage(scores),
      grade: gradeOf(scores),
    };
  });
}

export interface EvaluationSummary {
  people: number;
  /** Số người đã chấm đủ ba tiêu chí. */
  rated: number;
  averages: EvaluationScores;
  overall: number | null;
}

/** Điểm trung bình từng tiêu chí trên những người đã chấm tiêu chí đó. */
export function evaluationSummary(rows: readonly EvaluationRow[]): EvaluationSummary {
  const mean = (values: number[]) =>
    values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  const of = (key: EvaluationCriterion) =>
    mean(
      rows
        .map((row) => row.evaluation?.scores[key] ?? null)
        .filter((value): value is number => value !== null),
    );
  return {
    people: rows.length,
    rated: rows.filter((row) => row.evaluation && isComplete(row.evaluation.scores)).length,
    averages: { skill: of('skill'), compliance: of('compliance'), attitude: of('attitude') },
    overall: mean(rows.map((row) => row.average).filter((v): v is number => v !== null)),
  };
}

/** Bảng đánh giá tuần thành CSV (mở bằng Excel). */
export function evaluationCsv(
  rows: readonly EvaluationRow[],
  weekLabel: string,
  displayName: (username: string) => string,
): string {
  return toCsv([
    [
      'Tuần',
      'Nhân viên',
      'Cấp bậc',
      ...EVALUATION_CRITERIA.map((c) => c.label),
      'Điểm TB',
      'Xếp loại',
      'Nhận xét',
    ],
    ...rows.map((row) => [
      weekLabel,
      displayName(row.username),
      row.rank,
      ...EVALUATION_CRITERIA.map((c) => {
        const value = row.evaluation?.scores[c.key] ?? null;
        return value === null ? '' : String(value);
      }),
      row.average === null ? '' : String(Math.round(row.average * 10) / 10),
      row.grade?.label ?? '',
      row.evaluation?.note ?? '',
    ]),
  ]);
}

// ------------------------------------------------------------------ chi tiết một người

export interface KpiHistoryWeek {
  weekKey: string;
  rangeLabel: string;
  /** Tổng KPI (sản phẩm) đặt trong tuần, mọi dự án. */
  kpi: number;
  hours: number;
  capacity: number;
  scores: EvaluationScores;
  average: number | null;
  grade: Grade | null;
  note: string;
}

/** Lịch sử các tuần của một người, TUẦN MỚI NHẤT TRƯỚC — KPI và giờ cộng mọi dự án (kể cả đã xong). */
export function kpiHistory(
  username: string,
  projects: readonly Project[],
  weeks: readonly KpiWeek[],
  evaluations: readonly KpiEvaluation[],
  staff: readonly KpiStaff[],
  columns: readonly WeekColumn[],
): KpiHistoryWeek[] {
  const hoursPerUnit = new Map(projects.map((project) => [project.recordId, project.hoursPerUnit]));
  const coefficient = latestBy(staff, (item) => item.username).get(username)?.coefficient ?? 1;
  const weekByKey = latestBy(
    weeks.filter((week) => week.username === username),
    (week) => `${week.weekKey}|${week.projectId}`,
  );
  const evaluationByWeek = latestBy(
    evaluations.filter((item) => item.username === username),
    (item) => item.weekKey,
  );
  return [...columns].reverse().map((column) => {
    let kpi = 0;
    let hours = 0;
    for (const week of weekByKey.values()) {
      if (week.weekKey !== column.key) continue;
      kpi += week.kpi;
      hours += week.kpi * (hoursPerUnit.get(week.projectId) ?? 0);
    }
    const scores = evaluationByWeek.get(column.key)?.scores ?? EMPTY_SCORES;
    return {
      weekKey: column.key,
      rangeLabel: column.rangeLabel,
      kpi,
      hours,
      capacity: STANDARD_WEEK_HOURS * coefficient,
      scores,
      average: evaluationAverage(scores),
      grade: gradeOf(scores),
      note: evaluationByWeek.get(column.key)?.note ?? '',
    };
  });
}
